import { PLATFORM_ROLE_KEYS, type PlatformMember, type PlatformRoleKey } from '@sentinel/shared';
import { Button, InputField, Panel, SelectField, Table, type Column } from '@sentinel/ui';
import { formatDate, QueryState, toast, useAuthStore, useErrorMessage } from '@sentinel/web-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { consoleEndpoints } from '@/api/endpoints';
import { keys } from '@/api/keys';
import { useConsoleCan } from '@/auth/use-console-can';

const K = 'console.team';

/** The platform team: who holds which platform role. The server keeps the grade rules (never
 *  one's own role, never above one's grade, always one owner). */
export function TeamPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const canManage = useConsoleCan('console.members:manage');
  const myId = useAuthStore((s) => s.me?.id);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PlatformRoleKey>('platform_support');
  const members = useQuery({ queryKey: keys.members, queryFn: consoleEndpoints.members });
  const onSuccess = (message: string) => (data: { items: PlatformMember[] }) => {
    queryClient.setQueryData(keys.members, data);
    toast('ok', t(message));
  };
  const onError = (error: unknown) => toast('att', errorMessage(error));
  const upsert = useMutation({
    mutationFn: () => consoleEndpoints.upsertMember(email.trim(), role),
    onSuccess: onSuccess(`${K}.saved`),
    onError,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => consoleEndpoints.removeMember(userId),
    onSuccess: onSuccess(`${K}.removed`),
    onError,
  });

  const columns: Column<PlatformMember>[] = [
    {
      key: 'who',
      header: t(`${K}.columns.person`),
      render: (m) => (
        <>
          <b>{m.displayName}</b>
          <span className="sub">{m.email}</span>
        </>
      ),
    },
    { key: 'role', header: t(`${K}.columns.role`), render: (m) => t(`console.roles.${m.role}`) },
    {
      key: 'since',
      header: t(`${K}.columns.since`),
      mono: true,
      render: (m) => formatDate(m.createdAt),
    },
    {
      key: 'act',
      header: '',
      render: (m) =>
        canManage && m.userId !== myId ? (
          <Button
            size="sm"
            variant="danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate(m.userId)}
          >
            {t(`${K}.remove`)}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="split2">
      <Panel flush title={t(`${K}.title`)}>
        <QueryState query={members}>
          {(data) => (
            <Table
              caption={t(`${K}.title`)}
              columns={columns}
              rows={data.items}
              rowKey={(m) => m.userId}
            />
          )}
        </QueryState>
      </Panel>
      {canManage ? (
        <Panel title={t(`${K}.form.title`)}>
          <div className="stack">
            <p className="note">{t(`${K}.form.lead`)}</p>
            <InputField
              id="member-email"
              type="email"
              label={t(`${K}.form.email`)}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <SelectField
              id="member-role"
              label={t(`${K}.form.role`)}
              value={role}
              onChange={(e) => setRole(e.target.value as PlatformRoleKey)}
              options={PLATFORM_ROLE_KEYS.map((r) => ({
                value: r,
                label: t(`console.roles.${r}`),
              }))}
            />
            <Button
              style={{ alignSelf: 'flex-start' }}
              disabled={upsert.isPending || email.trim() === ''}
              onClick={() => upsert.mutate()}
            >
              {t(`${K}.form.submit`)}
            </Button>
          </div>
        </Panel>
      ) : null}
    </div>
  );
}
